import { useState, useEffect } from 'react';
import axios from 'axios';
import { fmt, colorByValue } from '../../utils/format';
import useLang from '../../hooks/useLang';
import { t } from '../../utils/i18n';

const api = axios.create({ baseURL: '/api', timeout: 15000 });

const moatColor = (rating) => {
  if (!rating) return 'var(--text-muted)';
  const r = rating.toLowerCase();
  if (r.includes('wide') || r.includes('宽') || r.includes('lebar')) return '#22c55e';
  if (r.includes('narrow') || r.includes('窄') || r.includes('sempit')) return '#eab308';
  return '#ef4444';
};

const moatBg = (rating) => {
  if (!rating) return 'transparent';
  const r = rating.toLowerCase();
  if (r.includes('wide') || r.includes('宽') || r.includes('lebar')) return '#22c55e22';
  if (r.includes('narrow') || r.includes('窄') || r.includes('sempit')) return '#eab30822';
  return '#ef444422';
};

// Inline moat badge shown in SnapshotHeader
export function MoatBadge({ symbol, onGoToMoat }) {
  const [moat, setMoat] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    setMoat(null);
    api.get(`/ai/moat/${symbol}`)
      .then(r => { if (r.data) setMoat(r.data); })
      .catch(() => {});
  }, [symbol]);

  if (!moat) return null;

  const color = moatColor(moat.moatRating);
  const bg = moatBg(moat.moatRating);

  return (
    <button
      onClick={onGoToMoat}
      title="View Economic Moat Analysis"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 20,
        background: bg, border: `1px solid ${color}`,
        cursor: 'pointer', fontSize: 12, fontWeight: 700, color,
        transition: 'opacity 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    >
      <span style={{ fontSize: 10 }}>🏰</span>
      {moat.moatRating}
      <span style={{ opacity: 0.7, fontWeight: 400, fontSize: 10 }}>{moat.overallScore}/10</span>
    </button>
  );
}

// Key metrics card for overview
export function KeyMetricsCard({ quote, fundamentals, valuation }) {
  const { lang } = useLang();
  const m = fundamentals?.metrics || {};
  const pct = (v) => v == null ? '—' : `${(v * 100).toFixed(1)}%`;
  const num = (v, dec = 2) => v == null ? '—' : v.toFixed(dec);
  const multiple = (v) => v == null ? '—' : `${v.toFixed(1)}x`;
  const large = (v) => {
    if (v == null) return '—';
    if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    return v.toLocaleString();
  };

  const rows = [
    { label: 'P/E Ratio (TTM)',             value: multiple(quote?.pe),                           color: null },
    { label: 'Forward P/E',                  value: multiple(quote?.forwardPE),                    color: null },
    { label: 'EPS Growth (YoY)',             value: m.epsGrowthYoY != null ? `${m.epsGrowthYoY > 0 ? '+' : ''}${m.epsGrowthYoY.toFixed(1)}%` : '—', color: colorByValue(m.epsGrowthYoY) },
    { label: 'Earnings Growth (Est.)',        value: pct(m.earningsGrowth),                        color: colorByValue(m.earningsGrowth) },
    { label: 'Dividend Yield (TTM)',          value: quote?.dividendYield != null ? `${(quote.dividendYield * 100).toFixed(2)}%` : '—', color: null },
    { label: 'Return on Equity (ROE)',        value: pct(m.roe),                                   color: colorByValue(m.roe) },
    { label: 'Return on Assets (ROA)',        value: pct(m.roa),                                   color: colorByValue(m.roa) },
    { label: 'Return on Capital (ROIC)',      value: pct(m.roic),                                  color: colorByValue(m.roic) },
    { label: 'FCF Yield',                     value: valuation?.fcfYield?.yieldPct != null ? `${valuation.fcfYield.yieldPct.toFixed(2)}%` : '—', color: colorByValue(valuation?.fcfYield?.yieldPct) },
    { label: 'Price / Book',                  value: multiple(m.priceToBook),                      color: null },
    { label: 'EV / EBITDA',                   value: multiple(m.evToEbitda),                       color: null },
    { label: 'Beta',                          value: quote?.beta != null ? num(quote.beta) : '—', color: null },
    { label: 'PEG Ratio',                     value: m.pegRatio != null ? num(m.pegRatio) : '—',  color: null },
    { label: 'Shares Outstanding',            value: large(quote?.sharesOutstanding ?? m.sharesOutstanding), color: null },
  ];

  const half = Math.ceil(rows.length / 2);
  const left = rows.slice(0, half);
  const right = rows.slice(half);

  const Row = ({ label, value, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: color || 'var(--text-primary)', fontFamily: 'JetBrains Mono, monospace' }}>{value}</span>
    </div>
  );

  return (
    <div className="card">
      <div className="card-title">Key Metrics</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
        <div>{left.map((r, i) => <Row key={i} {...r} />)}</div>
        <div>{right.map((r, i) => <Row key={i} {...r} />)}</div>
      </div>
    </div>
  );
}
